import throttle from "lodash-es/throttle";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { fetchAuthToken } from "app/Auth";
import { client } from "common/apiClient";
import {
  BuilderMap,
  GeneratedMapData,
  PieceStatus,
  BuilderBlockId,
  PieceEditHistoryEntry,
  PieceAiMessage,
  Difficulty,
} from "features/builder/types";

export type { GeneratedMapData };

export const getBuilderMaps = createAsyncThunk<
  BuilderMap[],
  {
    uid: string;
  }
>("builder/getBuilderMaps", async ({ uid }) => {
  const token = await fetchAuthToken();
  const response = await client({
    token: token,
  }).get(`/v1/builder/map?uid=${uid}`);
  return response.data;
});

export const createBuilderMap = createAsyncThunk<
  BuilderMap,
  {
    uid: string;
    name: string;
  }
>("builder/createBuilderMap", async ({ uid, name }) => {
  const token = await fetchAuthToken();
  const response = await client({
    token: token,
  }).post(`/v1/builder/map?uid=${uid}`, {
    name,
    content: { size: { x: 4, y: 4, z: 4 }, map: [] },
  });
  return response.data;
});

export const getBuilderMapContent = createAsyncThunk(
  "builder/getBuilderMapContent",
  async ({ builder_map_id }: { builder_map_id: string }) => {
    const token = await fetchAuthToken();
    const response = await client({
      token: token,
    }).get(`/v1/builder/map/content?builder_map_id=${builder_map_id}`);
    return response.data;
  },
);

export const updateBuilderMap = createAsyncThunk(
  "builder/updateBuilderMap",
  async ({
    uid,
    builder_map_id,
    map,
  }: {
    uid: string;
    builder_map_id: string;
    map: Partial<BuilderMap>;
  }) => {
    const token = await fetchAuthToken();
    const response = await client({
      token: token,
    }).put(`/v1/builder/map?uid=${uid}&builder_map_id=${builder_map_id}`, { ...map });
    return response.data;
  },
);

export const updateBuilderMapContent = createAsyncThunk(
  "builder/updateBuilderMapContent",
  throttle(async ({ uid }: { uid: string }, thunkAPI) => {
    const token = await fetchAuthToken();
    const { builder } = thunkAPI.getState() as RootState;
    try {
      await client({
        token: token,
      }).put(`/v1/builder/map/content?uid=${uid}&builder_map_id=${builder.editingBuilderMapId}`, {
        content: {
          size: builder.size,
          map: builder.map.entities,
        },
      });
    } catch (error) {
      console.error(error);
    }
  }, 5000),
);

export const deleteBuilderMap = createAsyncThunk(
  "builder/deleteBuilderMap",
  async ({ uid, builder_map_id }: { uid: string; builder_map_id: string }) => {
    const token = await fetchAuthToken();
    await client({
      token: token,
    }).delete(`/v1/builder/map/completely?uid=${uid}&builder_map_id=${builder_map_id}`);
    return builder_map_id;
  },
);

// AI マップ生成
interface GenerateMapRequest {
  prompt?: string;
  difficulty: Difficulty;
  size: { x: number; z: number };
}

export const generateMap = async (request: GenerateMapRequest): Promise<GeneratedMapData> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).post<GeneratedMapData>(
    "/v1/builder/map/generate",
    request,
  );
  return response.data;
};

// ===========================================================================
// /v1/pieces/* スタブ群（api-contract.md 準拠）
// TODO(piece-builder): API 未実装。実装後に client() 呼び出しへ差し替える。
// 現状はローカル動作のため console.warn + ダミー解決のみ。
// ===========================================================================

// POST /v1/pieces/:piece_id/complete（spec.md §5.8.3 / api-contract.md）
export const completePiece = async (params: {
  piece_id: string;
  uid: string;
  tested: boolean;
}): Promise<{ status: PieceStatus; version: number }> => {
  console.warn("[stub] POST /v1/pieces/:piece_id/complete", params);
  return { status: PieceStatus.UNPUBLISHED, version: 1 };
};

// POST /v1/pieces/:piece_id/publish
export const publishPiece = async (params: {
  piece_id: string;
  uid: string;
}): Promise<{ status: PieceStatus; version: number }> => {
  console.warn("[stub] POST /v1/pieces/:piece_id/publish", params);
  return { status: PieceStatus.PUBLISHED, version: 1 };
};

// POST /v1/pieces/:piece_id/draft
export const createDraft = async (params: {
  piece_id: string;
  uid: string;
}): Promise<{ draft_id: string }> => {
  console.warn("[stub] POST /v1/pieces/:piece_id/draft", params);
  return { draft_id: "local-draft" };
};

// DELETE /v1/pieces/:piece_id/draft
export const deleteDraft = async (params: { piece_id: string; uid: string }): Promise<void> => {
  console.warn("[stub] DELETE /v1/pieces/:piece_id/draft", params);
};

// GET /v1/pieces/:piece_id/draft
export const getDraftSnapshot = async (params: {
  piece_id: string;
  uid: string;
}): Promise<null> => {
  console.warn("[stub] GET /v1/pieces/:piece_id/draft", params);
  return null;
};

// PUT /v1/pieces/:piece_id/blockly（spec.md §5.8.7）
export const updatePieceBlockly = async (params: {
  piece_id: string;
  uid: string;
  enabled_blocks: BuilderBlockId[];
}): Promise<{ enabled_blocks: BuilderBlockId[] }> => {
  console.warn("[stub] PUT /v1/pieces/:piece_id/blockly", params);
  return { enabled_blocks: params.enabled_blocks };
};

// POST /v1/pieces/:piece_id/history（spec.md §5.8.4、5秒 throttle）
export const appendEditHistory = throttle(
  async (params: {
    piece_id: string;
    uid: string;
    actions: PieceEditHistoryEntry[];
  }): Promise<void> => {
    console.warn("[stub] POST /v1/pieces/:piece_id/history", params);
  },
  5000,
);

// GET /v1/pieces/:piece_id/history
export const getEditHistory = async (params: {
  piece_id: string;
  version?: number;
  draft_id?: string;
}): Promise<PieceEditHistoryEntry[]> => {
  console.warn("[stub] GET /v1/pieces/:piece_id/history", params);
  return [];
};

// POST /v1/pieces/ai/generate（新規 session + 初回生成）
export const aiGenerate = async (params: {
  uid: string;
  piece_id: string;
  prompt: string;
  difficulty: Difficulty;
  size: { x: number; z: number };
}): Promise<{ session_id: string; response_id: string; variations: GeneratedMapData[] }> => {
  console.warn("[stub] POST /v1/pieces/ai/generate", params);
  const variations = await Promise.all(
    Array.from({ length: 3 }).map(() =>
      generateMap({
        prompt: params.prompt || undefined,
        difficulty: params.difficulty,
        size: params.size,
      }),
    ),
  );
  return {
    session_id: `local-session-${Date.now()}`,
    response_id: `local-resp-${Date.now()}`,
    variations,
  };
};

// POST /v1/pieces/ai/refine（thread 継続。expired=true で 410 をシミュレート）
export const aiRefine = async (params: {
  uid: string;
  session_id: string;
  prompt: string;
  difficulty: Difficulty;
  size: { x: number; z: number };
  expired?: boolean;
}): Promise<{ session_id: string; response_id: string; variations: GeneratedMapData[] }> => {
  console.warn("[stub] POST /v1/pieces/ai/refine", params);
  if (params.expired) {
    const error = new Error("AI session expired");
    (error as Error & { status: number }).status = 410;
    throw error;
  }
  const variations = await Promise.all(
    Array.from({ length: 3 }).map(() =>
      generateMap({
        prompt: params.prompt || undefined,
        difficulty: params.difficulty,
        size: params.size,
      }),
    ),
  );
  return { session_id: params.session_id, response_id: `local-resp-${Date.now()}`, variations };
};

// POST /v1/pieces/ai/variations
export const aiVariations = async (params: {
  uid: string;
  prompt: string;
  difficulty: Difficulty;
  size: { x: number; z: number };
  count: number;
}): Promise<{ variations: GeneratedMapData[] }> => {
  console.warn("[stub] POST /v1/pieces/ai/variations", params);
  const count = Math.min(params.count, 3);
  const variations = await Promise.all(
    Array.from({ length: count }).map(() =>
      generateMap({
        prompt: params.prompt || undefined,
        difficulty: params.difficulty,
        size: params.size,
      }),
    ),
  );
  return { variations };
};

// GET /v1/pieces/ai/sessions/:session_id/messages
export const getAiMessages = async (params: { session_id: string }): Promise<PieceAiMessage[]> => {
  console.warn("[stub] GET /v1/pieces/ai/sessions/:session_id/messages", params);
  return [];
};

// PUT /v1/pieces/user/:uid/tutorial/:step_id（spec.md §5.8.6）
export const saveTutorialProgress = async (params: {
  uid: string;
  step_id: number;
}): Promise<void> => {
  console.warn("[stub] PUT /v1/pieces/user/:uid/tutorial/:step_id", params);
};

// POST /v1/pieces/:piece_id/play（テストプレイ完了）
export const playPiece = async (params: {
  piece_id: string;
  uid: string;
  cleared: boolean;
}): Promise<void> => {
  console.warn("[stub] POST /v1/pieces/:piece_id/play", params);
};
